// Copyright ©️ 2026 eVolve MEP, LLC

import type React from 'react';

// oxlint-disable-next-line no-restricted-imports
export type * from '@evolvemep/api-ts';

/**
 * Requires provided keys `Keys` to be defined
 * but allows the value to be nullish if the original value was nullish.
 */
export type WithRequiredIsh<T, Keys extends keyof T = keyof T> = Omit<T, Keys> & {
  [K in keyof Required<Pick<T, Keys>>]: T[K];
};

/**
 * Allow keys `Keys` on object `T` to be null
 */
export type WithNullable<T, Keys extends keyof T = keyof T> = Omit<T, Keys> & {
  [K in keyof Pick<T, Keys>]: T[K] | null;
};

/**
 * Make all keys on object `T` nullable except `Keys`
 */
export type WithNullableExcept<T, Keys extends keyof T = keyof T> = WithNullable<T, Exclude<keyof T, Keys>>;

/**
 * Requires _at least_ one of the keys of `T` to be non nil
 */
export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

type Indices<L extends number, T extends number[] = []> = T['length'] extends L
  ? T[number]
  : Indices<L, [T['length'], ...T]>;
type LengthAtLeast<E, T extends readonly E[], L extends number> = Pick<Required<T>, Indices<L>>;

/**
 * Validates `arr` has at least `len` values (type narrowed)
 */
export const hasLengthAtLeast = <E, T extends readonly E[], L extends number>(
  arr: T,
  /** must be >= 1 */
  len: L extends 0 ? never : L,
): arr is T & LengthAtLeast<E, T, L> => arr.length >= len;

/**
 * Validates `arr` has exactly `len` values (type narrowed)
 */
export const hasLengthExactly = <E, T extends readonly E[], L extends number>(
  arr: T,
  /** must be >= 0 */
  len: L,
): arr is T & LengthAtLeast<E, T, L> & { length: L } => arr.length === len;

/**
 * Wrapper for the "setter" return type of `useState`
 */
export type UseState<T> = React.Dispatch<React.SetStateAction<T>>;
